import React from 'react';
import styled from 'styled-components';
import { FilterPill, Tooltip } from 'venn-ui-kit';
import { LIBRARY_ITEM_TYPE_LABELS, QUICK_FILTER_LABELS } from '../../getFilters';
import { FilterType, FundUtils } from 'venn-utils';
import type { AssetTypeEnum, TaxStatusEnum } from 'venn-api';
import type { MorningstarCategory } from 'venn-state';
import { parseMorningstarCategory } from '../CategoryFilter';

const TAX_STATUS_LABELS: Record<TaxStatusEnum, string> = {
  TAXABLE: 'Taxable',
  TAX_DEFERRED: 'Tax-deferred',
  TAX_EXEMPT: 'Tax-exempt',
};

interface SelectedPillsProps<T extends string> {
  selected: Set<T>;
  onClick: (value: T) => void;
  iconType?: string;
  filterType?: FilterType;
}

const SelectedPills = <T extends string>({ selected, onClick, iconType, filterType }: SelectedPillsProps<T>) => {
  return (
    <Container>
      {Array.from(selected).map((value) => {
        const tooltip = getTooltip({ value, filterType });
        const pill = (
          <FilterPill name={renderValue({ value, filterType })} iconType={iconType} onClose={() => onClick(value)} />
        );

        return tooltip ? (
          <Tooltip key={value} content={tooltip} usePortal>
            {pill}
          </Tooltip>
        ) : (
          <span key={value}>{pill}</span>
        );
      })}
    </Container>
  );
};

interface renderValueProps {
  value: string;
  filterType: FilterType | undefined;
}

function renderValue({ value, filterType }: renderValueProps): string {
  switch (filterType) {
    case FilterType.SOURCE:
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      return QUICK_FILTER_LABELS[value];
    case FilterType.ASSET_TYPE:
      return FundUtils.getAssetTypeName(value as AssetTypeEnum);
    case FilterType.ITEM_TYPE:
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      return LIBRARY_ITEM_TYPE_LABELS[value];
    case FilterType.MORNINGSTAR_CATEGORY:
      return parseMorningstarCategory(value as MorningstarCategory).name;
    case FilterType.TAX_STATUS:
      return TAX_STATUS_LABELS[value as TaxStatusEnum];
    default:
      return value;
  }
}

function getTooltip({ value, filterType }: renderValueProps): string | undefined {
  if (filterType === FilterType.MORNINGSTAR_CATEGORY) {
    const { id } = parseMorningstarCategory(value as MorningstarCategory);
    return `Morningstar Category Code: ${id}`;
  }
  return undefined;
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 5px;
  > * {
    margin-top: 5px;
  }
`;

export default SelectedPills;
